import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { categories } from "@/mocks/videos";
import { getVideoById, getVideosByCategory } from "@/lib/videoUtils";
import { usePageMeta } from "@/hooks/usePageMeta";
import NotFound from "@/pages/NotFound";

export default function DetailPage() {
  const { videoId = "" } = useParams();
  const video = getVideoById(videoId);
  const [playing, setPlaying] = useState(false);

  usePageMeta({
    title: video ? `${video.title} - 多多视频` : "多多视频",
    description: video ? video.description : "多多视频影视详情",
    keywords: video
      ? `${video.title},${video.categoryName},${video.tags.join(",")},多多视频`
      : "多多视频",
  });

  if (!video) {
    return <NotFound />;
  }

  const related = getVideosByCategory(video.category)
    .filter((v) => v.id !== video.id)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 8);

  const infoItems = [
    { label: "类型", value: video.categoryName, icon: "ri-movie-2-line" },
    { label: "地区", value: video.region, icon: "ri-earth-line" },
    { label: "年代", value: String(video.year), icon: "ri-calendar-line" },
    { label: "时长", value: video.duration, icon: "ri-time-line" },
    { label: "更新", value: video.updatedAt, icon: "ri-refresh-line" },
  ];

  return (
    <div className="min-h-screen bg-background-950">
      <SiteNavbar />
      <main>
        <section className="relative h-[360px] w-full overflow-hidden md:h-[460px]">
          <img
            src={video.backdrop}
            alt={`${video.title} - 多多视频高清剧照`}
            title={`${video.title} - 多多视频`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />

          <div className="absolute inset-0">
            <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-8 md:px-6 md:pb-12">
              <nav aria-label="面包屑" className="mb-4 flex flex-wrap items-center gap-2 text-xs text-secondary-400 md:text-sm">
                <Link to="/" className="hover:text-accent-400 cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-base" />
                <Link to={`/category/${video.category}`} className="hover:text-accent-400 cursor-pointer">
                  {video.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line text-base" />
                <span className="text-foreground-200">{video.title}</span>
              </nav>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500/90 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {video.categoryName}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-background-800/80 px-2.5 py-1 text-xs font-semibold text-accent-400">
                  <i className="ri-star-fill text-xs" />
                  {video.rating.toFixed(1)}
                </span>
                <span className="rounded-md bg-background-800/80 px-2.5 py-1 text-xs font-medium text-foreground-200">
                  {video.status}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-50 md:text-5xl">
                {video.title}
              </h1>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-background-800 bg-background-900 p-4 sm:grid-cols-3 md:grid-cols-5 md:p-5">
                {infoItems.map((item) => (
                  <div key={item.label} className="min-w-0">
                    <div className="flex items-center gap-1 text-xs text-secondary-400">
                      <i className={`${item.icon} text-sm`} />
                      {item.label}
                    </div>
                    <div className="mt-1 truncate text-sm font-semibold text-foreground-100">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 overflow-hidden rounded-lg border border-background-800 bg-background-900">
                <div
                  className="relative flex h-[220px] items-center justify-center bg-background-950/60 md:h-[320px]"
                  style={{ backgroundImage: `url(${video.backdrop})`, backgroundSize: "cover", backgroundPosition: "top" }}
                >
                  <div className="absolute inset-0 bg-background-950/70" />
                  {playing ? (
                    <div className="relative z-10 flex flex-col items-center px-6 text-center ddv-fade-up">
                      <i className="ri-loader-4-line animate-spin text-4xl text-accent-400" />
                      <p className="mt-3 text-sm font-semibold text-foreground-50">
                        播放功能演示
                      </p>
                      <p className="mt-1 max-w-sm text-xs text-foreground-400">
                        当前为《{video.title}》播放演示效果，实际播放功能待后台资源接入后开放。
                      </p>
                      <button
                        type="button"
                        onClick={() => setPlaying(false)}
                        className="mt-4 whitespace-nowrap rounded-md border border-foreground-300/40 px-4 py-2 text-xs font-medium text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400 cursor-pointer"
                      >
                        停止演示
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPlaying(true)}
                      className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-950 transition-transform hover:scale-110 md:h-20 md:w-20 cursor-pointer"
                      aria-label="播放演示"
                    >
                      <i className="ri-play-fill text-3xl md:text-4xl" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-800 p-4 md:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="whitespace-nowrap rounded-md bg-accent-100/10 px-2.5 py-1 text-xs text-accent-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-600 cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg" />
                    播放演示
                  </button>
                </div>
              </div>

              <div className="mt-8">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-50 md:text-xl">
                  <i className="ri-file-text-line text-accent-400" />
                  剧情简介
                </h2>
                <p className="mt-3 text-sm leading-loose text-foreground-300 md:text-base">
                  {video.description}本片由多多视频精选推荐，画面高清、剧情扎实，适合喜欢{video.tags.join("、")}
                  题材的观众观看。更多相关精彩内容可在站内继续探索。
                </p>
              </div>

              <div className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-50 md:text-xl">
                  <i className="ri-magic-line text-accent-400" />
                  同类推荐
                </h2>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
                  {related.slice(0, 3).map((item) => (
                    <VideoCard key={item.id} video={item} />
                  ))}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-50">
                  <i className="ri-apps-2-line text-accent-400" />
                  分类快捷导航
                </h2>
                <div className="mt-4 flex flex-col gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                        cat.id === video.category
                          ? "bg-primary-500/15 text-primary-300"
                          : "text-foreground-300 hover:bg-background-800 hover:text-foreground-50"
                      }`}
                    >
                      <i className={`${cat.icon} text-lg`} />
                      <span className="flex-1">{cat.name}</span>
                      <i className="ri-arrow-right-s-line text-lg" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-50">
                  <i className="ri-fire-line text-accent-400" />
                  {video.categoryName}热播榜
                </h2>
                <ul className="mt-4 flex flex-col gap-1">
                  {related.slice(0, 5).map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-800 cursor-pointer"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            index < 3 ? "bg-accent-500 text-background-950" : "bg-background-800 text-secondary-400"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground-100">{item.title}</span>
                          <span className="text-xs text-secondary-400">
                            {item.region} · 评分 {item.rating.toFixed(1)}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}